// src/contexts/AuthProvider.jsx
// Optional auth for the game app — guest flow is always preserved.
// Adapted from apps/cellar/src/contexts/AuthProvider.jsx with game-specific additions:
//   - profile state (fetches profiles table)
//   - signInWithGoogle redirects back to current URL (preserves session code in URL)
//   - linkSessionPlayer: converts anonymous player row to authenticated record

import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '@tasted/utils/supabase';
import { consumeMagicLinkHash } from '@tasted/utils/magicLink';
import { identifyUser, track } from '@tasted/utils/analytics';
import { claimTokenStorage } from '../utils/playerStorage';
import { inboundAttributionProps } from '../utils/inboundAttribution';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // TAS-166: consume implicit-grant magic-link tokens from the URL hash no
  // matter which route the redirect landed on. The shared client runs
  // flowType 'pkce' and refuses to parse an implicit hash itself, and only
  // /welcome handled it manually — a login link that redirected to
  // `/?code=XXXX` left the user signed out with live tokens in the address
  // bar. setSession fires onAuthStateChange below; on /welcome the page's own
  // (earlier-running) consumer wins and this finds an empty hash.
  useEffect(() => {
    consumeMagicLinkHash();
  }, []);

  // ── Profile helpers ────────────────────────────────────────────────────────

  const fetchProfile = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, username, avatar_emoji, avatar_url, total_games')
      .eq('id', userId)
      .maybeSingle();
    if (!error && data) setProfile(data);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!user?.id) return;
    await fetchProfile(user.id);
  }, [user, fetchProfile]);

  // ── Identity merge (analytics) ─────────────────────────────────────────────

  const handleUserIdentityMerge = useCallback((authedUser) => {
    if (!authedUser) return;
    try {
      identifyUser(authedUser.id); // PII-free — no raw email/name to Mixpanel (TAS-73)
      track('User Authenticated', {
        userId: authedUser.id,
        provider: authedUser.app_metadata?.provider,
        isNewUser: authedUser.created_at === authedUser.last_sign_in_at,
      });
    } catch (err) {
      console.error('Identity merge failed:', err);
    }
  }, []);

  // ── Session bootstrap ──────────────────────────────────────────────────────

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const authedUser = session?.user || null;
      setUser(authedUser);
      setLoading(false);
      if (authedUser) {
        fetchProfile(authedUser.id);
        handleUserIdentityMerge(authedUser);
      }
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const authedUser = session?.user || null;
      setUser(authedUser);
      if (authedUser) {
        fetchProfile(authedUser.id);
        handleUserIdentityMerge(authedUser);
      } else {
        setProfile(null);
      }
    });

    return () => listener.subscription.unsubscribe();
  }, [fetchProfile, handleUserIdentityMerge]);

  // ── Auth methods ───────────────────────────────────────────────────────────

  const signIn = async (email, password) => {
    track('Sign In Attempted'); // no email to Mixpanel (TAS-73)
    const { error, data } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      track('Sign In Failed', { error: error.message });
      throw error;
    }
    setUser(data.user);
    return data.user;
  };

  const signUp = async (email, password, username) => {
    track('Sign Up Attempted'); // no email to Mixpanel (TAS-73)
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username },   // picked up by handle_new_user trigger
      },
    });
    if (error) {
      track('Sign Up Failed', { error: error.message });
      throw error;
    }
    setUser(data.user);
    return data.user;
  };

  const signInWithGoogle = async () => {
    track('Google Sign In Attempted');
    // redirectTo preserves the current URL so the session code (?code=XXXX) survives OAuth redirect
    const { error, data } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.href,
      },
    });
    if (error) {
      track('Google Sign In Failed', { error: error.message });
      throw error;
    }
    return data;
  };

  const signOut = async () => {
    track('User Sign Out', { userId: user?.id });
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
  };

  // ── Game-specific: link anonymous session player to auth profile ───────────
  // Called when an anonymous player registers/signs in (at the end-of-game
  // thank-you screen, or when they complete the magic-link on WelcomePage).
  //
  // The authoritative per-player score is computed HERE rather than trusted
  // from the caller: scores_v2 stores one row per (session, user, ROUND), so a
  // player's total is the SUM of their `points` across rounds — the previous
  // implementation trusted a caller-passed `score` that read a non-existent
  // `.score` column and only ever saved 0. `sessionUserId` is the session
  // participant id (session_users_v2.id === the game's currentPlayerId).
  //
  // Idempotent: if a game_scores row already exists for this profile+session
  // (e.g. both the thank-you screen and WelcomePage fire), it no-ops so the
  // score isn't double-counted and total_games isn't inflated.

  const linkSessionPlayer = async (sessionUserId, { sessionId: sessionIdArg, claimToken } = {}) => {
    if (!user?.id || !sessionUserId) return;

    const params = new URLSearchParams(window.location.search);
    const sessionId = sessionIdArg || params.get('code');
    if (!sessionId) return;

    // Server-authoritative claim (TAS-133 / migration 049). The SECURITY DEFINER
    // `claim_game_score` RPC binds the session player to this profile, recomputes
    // the score from scores_v2 server-side (the client can no longer supply it),
    // writes game_scores atomically (idempotent via UNIQUE(profile_id,
    // session_id)), and recomputes profiles.total_games — all in one call. It
    // rejects re-claiming a row already owned by another account. Throw on error
    // so callers don't render a "score saved" state over a silent failure.
    //
    // TAS-163: present the one-time claim token minted at join. Callers may
    // hand it in explicitly (WelcomePage carries it through the magic-link
    // redirect, which can open in a DIFFERENT browser); otherwise fall back to
    // this device's storage. Null is fine — the RPC only enforces the token
    // for rows that actually minted one.
    const token = claimToken || claimTokenStorage.get(sessionUserId) || null;
    const { data, error } = await supabase.rpc('claim_game_score', {
      p_session_id: sessionId,
      p_player_id: sessionUserId,
      p_claim_token: token,
    });
    if (error) throw error;

    // RPC returns a single row { score, wines_tasted }.
    const row = Array.isArray(data) ? data[0] : data;
    const score = Number(row?.score ?? 0);
    const winesTasted = Number(row?.wines_tasted ?? 0);

    await fetchProfile(user.id);
    // TAS-371: if this player arrived on a tokenised link, say so. A signup is
    // the end of a funnel that started somewhere, and without these the event
    // only ever said that one happened.
    track('Anonymous Player Converted', {
      sessionId, score, winesTasted, ...inboundAttributionProps(),
    });
  };

  // ── Claim the games played as a guest on this email address ────────────────
  // The in-app path above only works while the client still holds the session
  // code, the player id and the claim token. Make the account anywhere else —
  // the website, the app's own login, a link that lost its query string — and
  // the night is orphaned: session_users_v2.profile_id is never set and
  // get_leaderboard cannot see the player. Prod session 5017 is the example:
  // nine guests, and the winner made an account two minutes after the game
  // with his 36 points nowhere.
  //
  // claim_my_guest_scores (migration 192) takes no arguments. It matches the
  // address Supabase CONFIRMED for this account against the address that
  // player typed in at the table and binds the rows server-side, so nothing
  // here needs the token, the session or the player id.
  //
  // Never throws and never blocks the UI: this is a background repair, and a
  // player who has nothing to claim — the ordinary case — must not see an
  // error, or anything at all. An absent function (192 not applied yet) is a
  // caught rejection like any other.
  const claimGuestScores = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('claim_my_guest_scores');
      if (error) return [];
      const rows = Array.isArray(data) ? data : [];
      const linked = rows.filter((r) => r?.newly_linked);
      if (linked.length) {
        // No email, no token, no player id — a count and the sessions (TAS-73).
        track('Guest Scores Claimed', {
          sessions: linked.length,
          points: linked.reduce((sum, r) => sum + Number(r?.points ?? 0), 0),
        });
        if (user?.id) await fetchProfile(user.id);
      }
      return rows;
    } catch {
      return [];
    }
  }, [user, fetchProfile]);

  // Once per signed-in account per app load. onAuthStateChange fires again on
  // every token refresh, and a sweep per refresh would be a pointless round
  // trip — the work is idempotent, not free.
  const sweptRef = useRef(new Set());
  useEffect(() => {
    if (!user?.id || sweptRef.current.has(user.id)) return;
    sweptRef.current.add(user.id);
    claimGuestScores();
  }, [user, claimGuestScores]);

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      loading,
      signIn,
      signUp,
      signInWithGoogle,
      signOut,
      linkSessionPlayer,
      claimGuestScores,
      refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
